import { useSessionStore } from "@auth/sessionStore";
import { PatientClientName } from "@components/PatientClientName";
import { useUser } from "@context/UserContext";
import {
  faChevronDown,
  faClock,
  faClockRotateLeft,
  faLocationDot,
  faStopwatch,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRoomsList } from "@rooms/roomsQueries";
import {
  cn,
  FilingLine,
  IndexCard,
  Swipe,
  type SwipeTone,
  Tag,
} from "@scribenote/brian-design-system";
import { useNavigate } from "@tanstack/react-router";
import { canAccessReplay, canViewAttention } from "@users/userClient";
import {
  formatMonthDay,
  formatRelativeTime,
  formatTime,
} from "@utils/format-date";
import { useNow } from "@utils/use-now";
import {
  useWorkspaceNavigation,
  useWorkspaceSelection,
} from "@workspace/useWorkspaceNavigation";
import { useWorkspaceStore } from "@workspace/useWorkspaceStore";
import { useEffect, useRef } from "react";

import { FlowTracker } from "./FlowTracker";
import {
  isOnTodayBoard,
  isVisitCancelled,
  isVisitComplete,
  isVisitLate,
  type TodayVisit,
} from "./visitsClient";
import { prefetchVisitFlow } from "./visitsQueries";

/** Which highlighter a visit's state calls for. */
export type VisitTone =
  | "scheduled"
  | "working"
  | "stale"
  | "done"
  | "cancelled";

/**
 * The one place a visit's state becomes a color, so the band's marker and the
 * selected card's ring can never disagree. Built on the visitStatus predicates
 * (via visitsClient) plus the first-stage rule `isVisitLate` already uses:
 * position 0 still `pending` means the visit is booked but nobody has started
 * it. A visit with no stages at all hasn't started either.
 *
 * `stale` is a live visit left over from an earlier day (the Still open strip):
 * nothing on it is actively being worked however its stages read, so it wears
 * the flag, never the working green the strip's yellow would argue with.
 */
export const visitTone = (visit: TodayVisit): VisitTone => {
  if (isVisitCancelled(visit)) return "cancelled";
  if (isVisitComplete(visit)) return "done";
  // Guarded on the null: an unscheduled visit has no earlier day to be left
  // over from, and `isOnTodayBoard` reads null as off-board.
  if (visit.scheduledStart && !isOnTodayBoard(visit.scheduledStart))
    return "stale";
  const stage = visit.currentStage;
  if (!stage || (stage.status === "pending" && stage.position === 0))
    return "scheduled";
  return "working";
};

/**
 * The marker over the current stage name, or none: a finished or cancelled
 * visit is annotated in plain ink, never highlighted. Nothing is working on a
 * visit that's over.
 */
const toneSwipe: Record<VisitTone, SwipeTone | null> = {
  scheduled: "flag",
  working: "working",
  stale: "flag",
  done: null,
  cancelled: null,
};

/** The selected card's ring, matching the marker the visit wears. Settled
 * visits get quiet annotation ink rather than a status color. Flag rings use
 * the wash, not the ink: flag ink reads brown at ring width (the SubjectRail
 * SEVERITY_OPEN_BORDER precedent). */
const toneRing: Record<VisitTone, string> = {
  scheduled: "ring-wash-flag",
  working: "ring-ink-working",
  stale: "ring-wash-flag",
  done: "ring-annotation",
  cancelled: "ring-annotation",
};

/** Link treatment on paper for the expanded flow's entry points: indigo is
 * reserved for Brian/AI moments and focus rings, and these are plain
 * navigation, so the link is ink (a pen-underlined note on paper), not
 * accent. Hover darkens the ink and strengthens the underline; the focus
 * ring still rings indigo by system rule. */
const FLOW_LINK =
  "mt-2 flex cursor-pointer items-center gap-1.5 rounded-paper text-xs font-medium text-ink-body underline decoration-ink/30 decoration-1 underline-offset-2 transition-colors hover:text-ink hover:decoration-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

/**
 * One patient on the flow rail, as a physical index card on the desk: the
 * patient, a signalment stand-in (reason / visit type; species & breed are
 * Facts, out of scope), the scheduled time and room, all written on the ruling.
 * The card's bottom band names the current stage and doubles as the expander
 * that pulls the full stage tracker out from under the card.
 *
 * Two targets, two jobs: the body selects this patient, the band expands the
 * flow. Reads the workspace selection itself, so the open patient is lifted off
 * the desk and scrolls into view wherever it sits in the day's list (WEB-3288).
 */
export const PatientCard = ({ visit }: { visit: TodayVisit }) => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const expanded = useWorkspaceStore((s) => s.expandedVisitUuids.has(visit.id));
  const toggleExpanded = useWorkspaceStore((s) => s.toggleExpanded);
  const { selectedVisitUuid } = useWorkspaceSelection();
  const { selectVisitCard } = useWorkspaceNavigation();
  const selected = selectedVisitUuid === visit.id;
  const signalment = visit.reason ?? visit.visitType;
  const stage = visit.currentStage;
  const active = stage?.status === "active";
  const cancelled = isVisitCancelled(visit);
  const tone = visitTone(visit);
  const swipe = toneSwipe[tone];
  const onTodayBoard = isOnTodayBoard(visit.scheduledStart);
  const now = useNow();
  // Late is a today signal: on a visit left open for days it's true but useless,
  // and the date beside it already carries that.
  const late = onTodayBoard && isVisitLate(visit, now);

  const { data: rooms } = useRoomsList(sessionKey, true);
  const roomName = visit.currentRoomUuid
    ? (rooms.find((room) => room.id === visit.currentRoomUuid)?.name ?? null)
    : null;

  // Both targets warm the flow query, so expanding is instant however you got
  // to the card.
  const prefetch = () => prefetchVisitFlow(visit.id);

  // `nearest`: only scrolls when the card is actually out of view.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected) cardRef.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  return (
    <IndexCard
      ref={cardRef}
      className={cn(
        "transition duration-200 ease-paper",
        // Paper resting on the espresso desk casts a float shadow, not the
        // page-on-page lift IndexCard bakes in; `cn` merges, so this displaces
        // the card's resting shadow.
        //
        // The ring is the visit's own highlighter ink (see toneRing), not the
        // indigo accent: selection says "this one" in the color the card is
        // already wearing, and indigo stays the ai's, plus the focus ring's.
        selected
          ? cn("-translate-y-0.5 shadow-float ring-[3px]", toneRing[tone])
          : "hover:-translate-y-px hover:shadow-float",
        cancelled && "opacity-60",
      )}
      band={
        <>
          <button
            type="button"
            onClick={() => toggleExpanded(visit.id)}
            onPointerEnter={prefetch}
            onFocus={prefetch}
            aria-expanded={expanded}
            className="flex w-full cursor-pointer items-center gap-2 rounded-paper px-3 py-1 text-left focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent"
          >
            {active && (
              <span
                aria-hidden="true"
                className={cn(
                  "size-1.5 shrink-0 rounded-full [animation:bds-pulse_1.6s_ease-in-out_infinite] motion-reduce:animate-none",
                  // A stale visit's "active" stage is where it stopped, not
                  // live work: the dot flags instead of claiming progress.
                  tone === "stale"
                    ? "bg-wash-flag ring-1 ring-ink-flag"
                    : "bg-wash-working ring-1 ring-ink-working",
                )}
              />
            )}
            <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-ink-body">
              {stage ? (
                // Marked while the visit is live, plain once it's over: no
                // highlighter over a visit that finished or didn't happen.
                swipe ? (
                  <Swipe tone={swipe}>{stage.name}</Swipe>
                ) : (
                  <span className="text-annotation">{stage.name}</span>
                )
              ) : (
                "View flow"
              )}
            </span>
            {tone === "done" && (
              // The stage name alone doesn't say the visit is over, and a
              // finished visit gets no marker, so the state is written out.
              <span className="shrink-0 text-annotation-caps text-annotation">
                Done
              </span>
            )}
            <FontAwesomeIcon
              icon={faChevronDown}
              aria-hidden="true"
              className={cn(
                "shrink-0 text-fine-print text-annotation transition-transform",
                expanded && "rotate-180",
              )}
            />
          </button>
          {expanded && (
            // The flow opens as a well under the card's bottom band, so the
            // ruled face keeps saying only what's written on the card. `-mx-1`
            // cancels the band's padding so the fold rule spans the full width.
            <div className="-mx-1 border-t border-rule px-4 pt-2 pb-1">
              <FlowTracker visitUuid={visit.id} stale={tone === "stale"} />
              {canAccessReplay(currentUser) && (
                <button
                  type="button"
                  onClick={() =>
                    navigate({ to: "/replay", search: { visit: visit.id } })
                  }
                  data-testid={`card-replay-${visit.id}`}
                  className={FLOW_LINK}
                >
                  <FontAwesomeIcon
                    icon={faClockRotateLeft}
                    aria-hidden="true"
                  />
                  Visit replay
                </button>
              )}
              {canViewAttention(currentUser) && (
                <button
                  type="button"
                  onClick={() =>
                    navigate({ to: "/attention", search: { visit: visit.id } })
                  }
                  data-testid={`card-attention-${visit.id}`}
                  className={FLOW_LINK}
                >
                  <FontAwesomeIcon icon={faStopwatch} aria-hidden="true" />
                  View attention
                </button>
              )}
            </div>
          )}
        </>
      }
    >
      {/*
       * Click the body to select this patient (toggles off if re-clicked),
       * distinct from the band below, which expands the stage tracker.
       *
       * Full-bleed on ruled paper: `-m-4` cancels the writing area's padding so
       * the button reaches the card's edges, and the matching `p-4` puts the
       * text back exactly where the ruling expects it. Change one and you
       * change the other. The width restates that same 2rem: a button sizes to
       * its content whatever its `display`, so without it the lines below
       * stretch the card instead of truncating (WEB-3343).
       * Line-height stays `var(--ruling)` so every line
       * still occupies exactly one rule; the smaller lines below inherit that
       * computed 24px rather than setting their own.
       */}
      <button
        type="button"
        onClick={() => selectVisitCard(selected ? null : visit.id)}
        onPointerEnter={prefetch}
        onFocus={prefetch}
        aria-pressed={selected}
        className="-m-4 block w-[calc(100%+2rem)] cursor-pointer p-4 text-left leading-[var(--ruling)] focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent"
      >
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate font-display text-[1.125rem] font-medium text-ink">
            <PatientClientName
              patientName={visit.patientName}
              clientName={visit.clientName}
              clientClassName="font-normal text-annotation"
            />
          </span>
          {cancelled && <Tag className="shrink-0">Canceled</Tag>}
        </span>
        <span className="block truncate text-[0.8125rem] text-ink-body">
          {signalment}
        </span>
        {(visit.scheduledStart || roomName) && (
          <FilingLine>
            {visit.scheduledStart && (
              <span
                // A bare time reads as today, so anything older carries its
                // date; the age goes in the tooltip, which costs no width.
                title={
                  onTodayBoard
                    ? undefined
                    : formatRelativeTime(visit.scheduledStart)
                }
                className="flex items-center gap-1.5"
              >
                <FontAwesomeIcon icon={faClock} />
                {late ? (
                  // Lateness is the time itself under the flag, not a word
                  // beside it (owner call); sr-only keeps it non-visual too.
                  <Swipe tone="flag">
                    {formatTime(visit.scheduledStart)}
                    <span className="sr-only"> (late)</span>
                  </Swipe>
                ) : onTodayBoard ? (
                  formatTime(visit.scheduledStart)
                ) : (
                  `${formatMonthDay(visit.scheduledStart)}, ${formatTime(visit.scheduledStart)}`
                )}
              </span>
            )}
            {roomName && (
              // A room name has no guaranteed break point (PIMS room codes are
              // often one token), so it needs its own min-w-0/break-words
              // rather than relying on the flex-wrap row alone.
              <span className="flex min-w-0 items-center gap-1.5">
                <FontAwesomeIcon icon={faLocationDot} className="shrink-0" />
                <span className="min-w-0 break-words">{roomName}</span>
              </span>
            )}
          </FilingLine>
        )}
      </button>
    </IndexCard>
  );
};
